'use client';

import {motion, MotionValue, useScroll, useTransform} from 'motion/react';
import {FC, useRef} from 'react';

import styles from './text-reveal.module.scss';

interface TextRevealByLetterProps {
    children: string;
    className?: string;
}

const TextRevealByLetter: FC<TextRevealByLetterProps> = ({children, className}) => {
    const targetRef = useRef<HTMLDivElement>(null);
    const {scrollYProgress} = useScroll({
        target: targetRef,
        offset: ['30% end', '150% end'],
    });

    const lines = children.split('$ ');
    const totalLetters = lines.reduce((acc, line) => acc + line.length, 0);

    return (
        <div ref={targetRef} className={[styles.container, className].join(' ')}>
            <div className={styles.contentWrapper}>
                <div className={`${styles.textContainer}`}>
                    {lines.map((line, i) => {
                        const letters = line.split('');
                        const offset = lines.slice(0, i).reduce((sum, l) => sum + l.length, 0);
                        const elements = getLineLetters(
                            letters,
                            totalLetters,
                            scrollYProgress,
                            offset,
                        );
                        return <div key={i}>{elements}</div>;
                    })}
                </div>
            </div>
        </div>
    );
};

const getLineLetters = (
    letters: string[],
    totalLetters: number,
    scrollYProgress: MotionValue<number>,
    offset: number,
) => {
    return letters.map((letter, i) => {
        const start = (i + offset) / totalLetters;
        const end = start + 1 / totalLetters;
        return (
            <Letter key={i} progress={scrollYProgress} range={[start, end]}>
                {letter}
            </Letter>
        );
    });
};

interface LetterProps {
    children: string;
    progress: MotionValue<number>;
    range: [number, number];
}

const Letter: FC<LetterProps> = ({children, progress, range}) => {
    const color = useTransform(progress, range, ['#474747', '#fff']);

    return (
        <span className={styles.word}>
            <span className={styles.wordPlaceholder}>{children}</span>
            <motion.span
                style={{color}}
                className={`${styles.wordText}`}
                dangerouslySetInnerHTML={{__html: children}}
            />
        </span>
    );
};

export {TextRevealByLetter};
